import { CreditNoteField } from '@components';
import { L } from '@lib';
import { ServiceOrderExtra } from '@services';
import { useStore } from '@stores';
import { Button, Checkbox, InputNumber, Modal, Select, Typography, message } from 'antd';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

const { Text } = Typography;

interface IProps {
  open: boolean;
  onClose: () => void;
}

// Must match the API's PartReturnDestinations.
const Destinations = { Stock: 'Stock', Supplier: 'Supplier' } as const;

const destinationOptions = [
  { label: L('Return to Stock'), value: Destinations.Stock },
  { label: L('Return to Supplier'), value: Destinations.Supplier },
];

const colLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#8c8c8c',
  fontWeight: 600,
};

export const ReturnPartsModal: React.FC<IProps> = observer(({ open, onClose }) => {
  const { orderStore, serviceStore } = useStore();
  const order = orderStore.order;

  // Flatten inventory-linked parts across all service orders on this job.
  const parts: ServiceOrderExtra[] = [];
  order?.serviceOrders?.forEach((so) =>
    so.extras?.forEach((e) => {
      if (e.inventoryId) parts.push(e);
    })
  );

  // Selected parts → destination, quantity, and (for supplier returns) an optional credit note.
  // Presence of a key = selected.
  type Selection = {
    destination: string;
    quantity: number;
    creditNoteId?: string;
    creditNoteName?: string;
  };
  const [selections, setSelections] = useState<Record<string, Selection>>({});
  const [submitting, setSubmitting] = useState(false);

  const returnable = parts;
  const selectedIds = Object.keys(selections);
  const allChecked = returnable.length > 0 && selectedIds.length === returnable.length;
  const indeterminate = selectedIds.length > 0 && selectedIds.length < returnable.length;

  const reset = () => {
    setSelections({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const toggleAll = (checked: boolean) =>
    setSelections(
      checked
        ? returnable.reduce(
            (acc, p) => ({
              ...acc,
              [p.id]: { destination: Destinations.Stock, quantity: p.quantity },
            }),
            {}
          )
        : {}
    );

  const toggleOne = (id: string, checked: boolean, maxQty: number) =>
    setSelections((prev) => {
      if (checked) return { ...prev, [id]: { destination: Destinations.Stock, quantity: maxQty } };
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const setDestination = (id: string, value: string) =>
    setSelections((prev) => ({ ...prev, [id]: { ...prev[id], destination: value } }));

  const setQuantity = (id: string, value: number) =>
    setSelections((prev) => ({ ...prev, [id]: { ...prev[id], quantity: value } }));

  const setCreditNote = (id: string, creditNoteId?: string, creditNoteName?: string) =>
    setSelections((prev) => ({ ...prev, [id]: { ...prev[id], creditNoteId, creditNoteName } }));

  const doReturn = async () => {
    const items = Object.entries(selections)
      .map(([id, sel]) => {
        const part = parts.find((p) => p.id === id);
        return part
          ? {
              id: part.id,
              destination: sel.destination,
              quantity: sel.quantity,
              documentId: sel.creditNoteId,
            }
          : null;
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);

    if (!items.length) {
      message.warning(L('Select at least one part to return.'));
      return;
    }

    setSubmitting(true);
    try {
      // One atomic batch — the server returns all lines or none, so no partial state to reconcile.
      const results = await orderStore.returnExtraParts(items);
      if (!results) {
        message.error(L('Parts could not be returned. Please try again.'));
        return;
      }
      // Mirror the new quantities into the editable parts list the order page actually renders.
      results.forEach((r) => {
        const so = order?.serviceOrders?.find((s) => s.id === r.serviceOrderId);
        if (so?.serviceId != null) {
          serviceStore.syncReturnedPart(so.serviceId, r.inventoryId, r.quantity);
        }
      });
      message.success(L('Parts returned'));
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={L('Return Parts')}
      width={680}
      onCancel={handleClose}
      footer={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <div style={{ textAlign: 'left', fontSize: 11, lineHeight: 1.5, color: '#8c8c8c' }}>
            <div>
              {L("Return to Supplier – Returned quantities won't appear in Stock Inventory.")}
            </div>
            <div>
              {L('Return to Stock – Returned quantities will be added back to Stock Inventory.')}
            </div>
          </div>
          <div style={{ flexShrink: 0, display: 'flex', gap: 8 }}>
            <Button key="cancel" onClick={handleClose}>
              {L('Close')}
            </Button>
            <Button
              key="return"
              type="primary"
              onClick={doReturn}
              loading={submitting}
              disabled={!selectedIds.length}
            >
              {L('Return selected')}
            </Button>
          </div>
        </div>
      }
    >
      {parts.length === 0 ? (
        <Text type="secondary">{L('No inventory-linked parts on this job.')}</Text>
      ) : (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
            {L(
              'Pick which parts to return, how many, and where each one goes. Each part can take its own credit note.'
            )}
          </Text>
          <div style={{ marginBottom: 8 }}>
            <Checkbox
              indeterminate={indeterminate}
              checked={allChecked}
              disabled={returnable.length === 0}
              onChange={(e) => toggleAll(e.target.checked)}
            >
              {L('Select all')}
            </Checkbox>
          </div>

          {/* Column headers — label the qty + destination inputs below */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '0 0 6px',
              borderBottom: '1px solid #e8e8e8',
            }}
          >
            <span style={{ ...colLabelStyle, flex: 1, minWidth: 0 }}>{L('Part')}</span>
            <span style={{ ...colLabelStyle, width: 100, flexShrink: 0 }}>
              {L('Qty to return')}
            </span>
            <span style={{ ...colLabelStyle, width: 190, flexShrink: 0 }}>{L('Destination')}</span>
          </div>

          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {parts.map((p) => {
              const sel = selections[p.id];
              const checked = !!sel;
              return (
                <div key={p.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Checkbox
                      checked={checked}
                      onChange={(e) => toggleOne(p.id, e.target.checked, p.quantity)}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      {p.name} <Text type="secondary">× {p.quantity}</Text>
                    </Checkbox>
                    <InputNumber
                      size="small"
                      style={{ width: 100, flexShrink: 0 }}
                      min={Math.min(1, p.quantity)}
                      max={p.quantity}
                      step={1}
                      disabled={!checked}
                      value={sel?.quantity ?? p.quantity}
                      onChange={(v) => setQuantity(p.id, Number(v) || Math.min(1, p.quantity))}
                    />
                    <Select
                      size="small"
                      style={{ width: 190, flexShrink: 0 }}
                      options={destinationOptions}
                      value={sel?.destination ?? Destinations.Stock}
                      disabled={!checked}
                      onChange={(v) => setDestination(p.id, v)}
                    />
                  </div>

                  {checked && (
                    <div style={{ paddingLeft: 24, marginTop: 4 }}>
                      <CreditNoteField
                        documentId={sel?.creditNoteId}
                        fileName={sel?.creditNoteName}
                        onChange={(id, name) => setCreditNote(p.id, id, name)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
});

export default ReturnPartsModal;
